import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import authService from '../api/authService.js';

export type UserRole = 'professional' | 'recruiter' | 'admin';

export interface AuthUser {
  id: number;
  email: string;
  role: UserRole;
  full_name: string;
  is_email_verified: boolean;
  phone_number: string;
  profile_picture: string | null;
  last_seen: string | null;
  profile_completeness: number;
  is_profile_hidden: boolean;

  // Recruiter-only fields
  company_name?: string | null;
  company_website?: string | null;
  company_logo?: string | null;
  company_verification_status?: string | null;
  country?: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}


interface AuthState {
  // State
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: AuthUser | null) => void;
  setToken: (token: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  login: (credentials: LoginCredentials) => Promise<AuthUser>;
  logout: () => void;

  // Convenience selectors
  profileCompletion: () => number;
  isProfessional: () => boolean;
  isRecruiter: () => boolean;
}


export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: localStorage.getItem('authToken'),
        isLoading: !!localStorage.getItem('authToken'),
        isAuthenticated: !!localStorage.getItem('authToken'),

        setUser: (user) =>
          set(
            { user, isAuthenticated: !!user, isLoading: false },
            false,
            'setUser'
          ),

        setToken: (token) => {
          if (token) {
            localStorage.setItem('authToken', token);
          } else {
            localStorage.removeItem('authToken');
          }
          set({ token, isAuthenticated: !!token }, false, 'setToken');
        },

        setIsLoading: (isLoading) => set({ isLoading }, false, 'setIsLoading'),

        login: async (credentials) => {
          const data = await authService.login(credentials);
          get().setToken(data.access);
          get().setUser(data.user);
          return data.user;
        },

        logout: () => {
          authService.logout();
          localStorage.removeItem('authToken');
          // Intentionally do NOT persist old user data
          set(
            { user: null, token: null, isAuthenticated: false, isLoading: false },
            false,
            'logout'
          );
        },

        // ── Derived / selectors ────────────────────────────────────────────
        // Usage: const completion = useAuthStore(s => s.profileCompletion())
        profileCompletion: () => get().user?.profile_completeness ?? 0,

        isProfessional: () => get().user?.role === 'professional',

        isRecruiter: () => get().user?.role === 'recruiter',
      }),
      {
        name: 'winguport-auth',
        // Only persist the token — never cache the full user object.
        // Fresh user data always comes from react-query on mount.
        partialize: (state) => ({ token: state.token }),
      }
    ),
    { name: 'AuthStore' }
  )
);